/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { Left, Right } from 'purify-ts/Either';
import { EitherAsync } from 'purify-ts/EitherAsync';
import { always } from 'purify-ts/Function';
import { Maybe } from 'purify-ts/Maybe';
import { getStoreBuilder } from 'vuex-typex';
import * as api from '@codegrade/api';
import client from '@/client';
import * as models from '@/models';
import { SubmissionsStore } from '@/store';
import * as utils from '@/utils';
import { Counter } from '@/utils/counter';
const storeBuilder = getStoreBuilder();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function createEmptyLoaders() {
    return {
        feedback: {},
        inlineFeedbackByUser: {},
    };
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function createState() {
    return {
        feedbacks: {},
        inlineFeedbackByUser: {},
        loaders: createEmptyLoaders(),
    };
}
const moduleBuilder = storeBuilder.module('feedback', createState());
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var FeedbackStore;
(function (FeedbackStore) {
    /* eslint-disable-next-line no-underscore-dangle, jsdoc/require-jsdoc -- When touching please add documentation. */
    function _getFeedback(state) {
        return (assignmentId, submissionId) => state.feedbacks?.[assignmentId]?.[submissionId];
    }
    const commitFeedbackLoader = moduleBuilder.commit((state, payload) => {
        payload.loader.caseOf({
            Just: loader => utils.vueSet(state.loaders.feedback, payload.submissionId, loader),
            Nothing: () => utils.vueDelete(state.loaders.feedback, payload.submissionId),
        });
    }, 'commitFeedbackLoader');
    const commitInlineFeedbackyByUserLoader = moduleBuilder.commit((state, payload) => {
        const { assignmentId, userId } = payload;
        const mapping = utils.vueSetDefault(state.loaders.inlineFeedbackByUser, assignmentId, {});
        payload.loader.caseOf({
            Just: loader => utils.vueSet(mapping, userId, loader),
            Nothing: () => utils.vueDelete(mapping, userId),
        });
    }, 'commitInlineFeedbackyByUserLoader');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.getFeedback = moduleBuilder.read(_getFeedback, 'getFeedback');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.getSubmissionWithFeedbackByUser = moduleBuilder.read(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    state => (assignmentId, userId) => Maybe.fromNullable(state.inlineFeedbackByUser[assignmentId]?.[userId]), 'getSubmissionWithFeedbackByUser');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.commitSetFeedback = moduleBuilder.commit((state, payload) => {
        const trees = state.feedbacks[payload.assignmentId] ?? {};
        utils.vueSet(trees, payload.submissionId, payload.feedback);
        utils.vueSet(state.feedbacks, payload.assignmentId, trees);
    }, 'commitSetFeedback');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.commitDeleteFeedback = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        utils.vueDelete(state.feedbacks, payload.assignmentId);
        utils.vueDelete(state.inlineFeedbackByUser, payload.assignmentId);
    }, 'commitDeleteFeedback');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.commitDeleteAllFeedback = moduleBuilder.commit(state => {
        Object.assign(state, createState());
    }, 'commitDeleteAllFeedback');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.commitUpdateFeedback = moduleBuilder.commit((state, payload) => {
        const { assignmentId, submissionId, feedback, invalidateCache } = payload;
        utils.vueSet(state.feedbacks[assignmentId], submissionId, feedback);
        const byUserForAssig = state.inlineFeedbackByUser[assignmentId];
        if (invalidateCache !== false && byUserForAssig != null) {
            feedback.authorIds.forEach(authorId => {
                utils.vueDelete(byUserForAssig, authorId);
            });
        }
    }, 'commitUpdateFeedback');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const getCommentCountOfUser = (payload) => {
        const { userId, comments } = payload;
        const counter = Counter.make(comments, comment => comment.workId, comment => utils.sumMap(comment.replies, reply => (reply.authorId === userId ? 1 : 0)));
        return counter.toMap();
    };
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.commitSetFeedbackByUser = moduleBuilder.commit((state, payload) => {
        const { assignmentId, userId, mapping } = payload;
        if (state.inlineFeedbackByUser[assignmentId] == null) {
            utils.vueSet(state.inlineFeedbackByUser, assignmentId, {});
        }
        utils.vueSet(state.inlineFeedbackByUser[assignmentId], userId, mapping);
    }, 'commitSetFeedbackByUser');
    const commitClearFeedback = moduleBuilder.commit(state => {
        Object.assign(state, createState());
    }, 'commitClearFeedback');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.loadFeedback = moduleBuilder.dispatch(({ state }, { assignmentId, submissionId, force = false, }) => utils.loadApiWithMaybeForce({
        force,
        value: Maybe.fromNullable(FeedbackStore.getFeedback()(assignmentId, submissionId)),
        loader: Maybe.fromNullable(state.loaders.feedback[submissionId]),
        addLoader: loader => commitFeedbackLoader({ submissionId, loader: Maybe.of(loader) }),
        removeLoader: () => commitFeedbackLoader({ submissionId, loader: Maybe.empty() }),
        load: () => utils.eitherAsyncUtils
            .all([
            client.submission
                .getFeedback({ submissionId, withReplies: true })
                .chainLeft(err => {
                if (err instanceof api.ApiResultError &&
                    err.response.status === 403) {
                    return utils.eitherAsyncUtils.rightFromValue(err.response.map(always(undefined)));
                }
                return utils.eitherAsyncUtils.leftFromValue(err);
            }),
            SubmissionsStore.loadSingleSubmission({
                assignmentId,
                submissionId,
            }),
        ])
            .chain(([response]) => EitherAsync.liftEither(response.mapEither(cgResult => {
            if (cgResult?.type === 'feedback-with-replies') {
                const feedback = models.Feedback.fromApiServerData(cgResult);
                FeedbackStore.commitSetFeedback({
                    assignmentId,
                    submissionId,
                    feedback,
                });
                return Right(feedback);
            }
            utils.AssertionError.typeAssert(cgResult);
            return Left(new Error('Expected a FeedbackWithReplies'));
        }))),
    }), 'loadFeedback');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.createFeedbackThread = moduleBuilder.dispatch((_, payload) => {
        const { assignmentId, submissionId, userId, location } = payload;
        const requestBody = location ?? { workId: submissionId };
        return utils.eitherAsyncUtils
            .all([
            client.comment.createBase({ requestBody }),
            FeedbackStore.loadFeedback({ assignmentId, submissionId }),
        ])
            .map(([response, feedback]) => response.map(cgResult => {
            const feedbackLine = models.FeedbackThread.fromApiServerData(cgResult).createReply(userId);
            FeedbackStore.commitUpdateFeedback({
                assignmentId,
                submissionId,
                feedback: feedback.cgResult.updateThread(feedbackLine),
            });
            return feedbackLine;
        }));
    }, 'createFeedbackLline');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.updateThread = moduleBuilder.dispatch((_, payload) => {
        const { assignmentId, submissionId, thread, invalidateCache } = payload;
        return FeedbackStore.loadFeedback({ assignmentId, submissionId }).ifRight(response => FeedbackStore.commitUpdateFeedback({
            assignmentId,
            submissionId,
            feedback: response.cgResult.updateThread(thread),
            invalidateCache,
        }));
    }, 'updateThread');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.loadInlineFeedbackByUser = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    ({ state }, payload) => {
        const { assignmentId, userId, force = false } = payload;
        return utils.loadApiWithMaybeForce({
            force,
            value: FeedbackStore.getSubmissionWithFeedbackByUser()(assignmentId, userId),
            loader: Maybe.fromNullable(state.loaders.inlineFeedbackByUser[assignmentId]?.[userId]),
            addLoader: loader => commitInlineFeedbackyByUserLoader({
                assignmentId,
                userId,
                loader: Maybe.of(loader),
            }),
            removeLoader: () => commitInlineFeedbackyByUserLoader({
                assignmentId,
                userId,
                loader: Maybe.empty(),
            }),
            load: () => client.assignment.getCommentsByUser({ assignmentId, userId }).map(response => response.map(cgResult => {
                const comments = cgResult.map(models.FeedbackThread.fromApiServerData);
                const mapping = getCommentCountOfUser({
                    userId,
                    comments,
                });
                FeedbackStore.commitSetFeedbackByUser({
                    assignmentId,
                    userId,
                    mapping,
                });
                return mapping;
            })),
        });
    }, 'loadInlineFeedbackByUser');
    utils.signal.AFTER_USER_LOGGED_OUT.register(() => {
        commitClearFeedback();
        FeedbackStore.commitDeleteAllFeedback();
    });
})(FeedbackStore || (FeedbackStore = {}));
